<template>
  <div class="generic-sample-card">
    <div class="generic-sample-card-header">
      <div class="generic-sample-icon">
        <div class="icon-background" />
        <slot name="icon"></slot>
      </div>
      <div class="generic-sample-card-title">
        <div class="sample-title-section">
          <h3>{{ getTranslatedSampleTypeName }}</h3>
          <p @click="navigateToSampleType">
            {{ $t('lastSamples.seeAll') }}
          </p>
        </div>
      </div>
    </div>
    <div class="generic-sample-card-status">
      <AlertsCard
        :sample-type="sampleType"
        :first-sample-type-key="getFirstSampleTypeKey"
      />
      <ReviewsCard
        :sample-type="sampleType"
        :first-sample-type-key="getFirstSampleTypeKey"
      />
      <InProcessCard
        :sample-type="sampleType"
        :first-sample-type-key="getFirstSampleTypeKey"
      />
    </div>
  </div>
</template>

<script>
import AlertsCard from '@/components/Dashboard/LastSamples/SampleTypesCard/AlertsCard.vue';
import ReviewsCard from '@/components/Dashboard/LastSamples/SampleTypesCard/ReviewsCard.vue';
import InProcessCard from '@/components/Dashboard/LastSamples/SampleTypesCard/InProcessCard.vue';

export default {
  name: 'GenericSampleTypeCard',
  components: {
    AlertsCard,
    ReviewsCard,
    InProcessCard,
  },
  props: {
    sampleType: { type: Object, required: true },
  },
  computed: {
    getSampleTypeKeys() {
      return Object.keys(this.sampleType || {});
    },
    getFirstSampleTypeKey() {
      return this.getSampleTypeKeys[0] || '';
    },
    // Mapeo de valores a claves de traducción
    serviceTypeTranslationMap() {
      return {
        Agronomía: 'Agronomy',
        Alimentaria: 'Food',
        'Medio Ambiente': 'Environment',
        Minería: 'Mining',
        'Salud y Seguridad': 'Health and Security',
        Agricultura: 'Agronomy',
        Ambiental: 'Environment',
      };
    },
    getTranslatedSampleTypeName() {
      const originalKey = this.getFirstSampleTypeKey;
      const translationKey =
        this.serviceTypeTranslationMap[originalKey] || originalKey;
      return this.$t(translationKey);
    },
  },
  methods: {
    navigateToSampleType() {
      const sampleTypesRouteMap = {
        Agronomía: 'AgronomySamplesPage',
        Alimentaria: 'FoodSamplesPage',
        'Medio Ambiente': 'EnvironmentSamplesPage',
        Minería: 'MiningSamplesPage',
        'Salud y Seguridad': 'HealthAndSecuritySamplesPage',
      };
      this.$f7router.navigate({
        name:
          sampleTypesRouteMap[this.getFirstSampleTypeKey] ??
          'DashboardHome',
      });
    },
  },
};
</script>

<style lang="scss">
.generic-sample-card {
  /* Layout */
  display: flex;
  width: 100%;
  padding: 7px 12px;
  align-items: center;
  gap: 18px;
  min-width: 0;
  box-sizing: border-box;
  /* Style */
  border-radius: 12px;
  background: var(--Neutral-10, #f7f7f8);

  /* Resetear estilos de headers */
  h3,
  h4,
  p {
    margin: 0;
    padding: 0;
  }

  /* Se cargan los estilos de las cards de los tipos de muestras */
  .sample-details-card {
    display: flex;
    align-items: center;
    gap: 14px;
    flex: 1 1 0;
    min-width: 0;
    min-height: 45px;
    padding: 7px 13px 7px 24px;
    position: relative;
    overflow: hidden;
    border-radius: 12px;
    border: 1px solid rgba(190, 187, 195, 0.72);
    background-color: white;
  }

  .sample-card-arrow {
    margin-left: auto;
    flex-shrink: 0;
    color: var(--Neutral-50, #9c9b9e) !important;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.12;
    transition: transform 0.2s ease, color 0.2s ease;
  }

  .sample-details-card.is-clickable {
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease,
      border-color 0.2s ease, background-color 0.2s ease;
  }

  .sample-details-card.is-clickable:hover {
    transform: translateY(-2px);
    box-shadow: 0px 10px 24px 0px rgba(4, 80, 159, 0.1);
    border-color: rgba(4, 80, 159, 0.18);
    background-color: #fff;

    .sample-card-arrow {
      transform: translateX(3px);
    }
  }

  .sample-details-card.is-clickable:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(4, 80, 159, 0.18);
    border-color: rgba(4, 80, 159, 0.35);
  }

  .generic-sample-card-header {
    display: flex;
    width: 240px;
    align-items: center;
    gap: 14px;
    flex-shrink: 0;
    min-width: 0;

    .generic-sample-icon {
      width: 34px;
      height: 34px;
      flex-shrink: 0;
      background-color: var(--Secondary-green-50, #abe723);
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      .icon-background {
        display: none;
      }
      svg {
        flex-shrink: 0;
      }
    }

    .generic-sample-card-title {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      min-height: 45px;
      min-width: 0;

      .sample-title-section {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        gap: 4px;
        min-width: 0;
        max-width: 100%;

        h3 {
          color: var(--Secondary-green-60, #72a300);
          font-size: 17px;
          font-style: normal;
          font-weight: 600;
          line-height: 126%;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        p {
          color: var(--Primary-Blue-70, #00346a);
          font-size: 14px;
          font-style: normal;
          font-weight: 400;
          line-height: 140%;
          text-decoration-line: underline;
          text-decoration-style: solid;
          text-decoration-skip-ink: none;
          text-decoration-thickness: auto;
          text-underline-offset: auto;
          text-underline-position: from-font;
          transition: color 0.2s ease;
          &:hover {
            cursor: pointer;
            color: var(--Primary-Blue-60, #04509f);
          }
        }
      }
    }
  }

  .generic-sample-card-status {
    display: flex;
    align-items: stretch;
    gap: 14px;
    flex: 1 1 0;
    min-width: 0;
  }

  .sample-alert-content,
  .sample-not-reviewed-content,
  .sample-in-process-content {
    min-width: 0;

    h4 {
      flex-shrink: 0;
    }

    p {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  @media (max-width: 1200px) {
    flex-direction: column;
    gap: 18px;
    align-items: stretch;

    .generic-sample-card-header {
      width: 100%;
    }

    .sample-details-card {
      min-height: 42px;
      padding: 7px 12px 7px 20px;
    }
  }

  @media (max-width: 768px) {
    padding: 14px;

    .generic-sample-card-status {
      flex-direction: column;
    }

    .sample-details-card {
      min-height: 42px;
      padding: 7px 10px 7px 14px;
    }
  }
}

.dashboard-grid-item-lead .generic-sample-card {
  gap: 12px;
  padding: 7px 10px;

  .generic-sample-card-header {
    width: 200px;
    gap: 10px;
  }

  .generic-sample-card-status {
    gap: 10px;
  }

  .sample-details-card {
    gap: 10px;
    padding: 7px 10px 7px 18px;
  }

  .sample-alert-content,
  .sample-not-reviewed-content,
  .sample-in-process-content {
    gap: 6px;
    min-width: 0;

    p {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .sample-card-arrow {
    font-size: 16px;
  }
}
</style>
